import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { WrappedErrorBoundary } from '@/pages/WrappedErrorBoundary';

import { useAppSelector } from '../../../app/hook';
import { useGetPlanInfoQuery, useRedirectToPayModMutation } from '../../../app/services/billing-rtk.service';
import LoadingComponent from '../../../components/common/loading/loading.component';
import ApiFailure from '../../../components/ui/ApiFailure';
import { MultiLanguageUrl } from '../../../constants/multiLanguage.constant';
import { ApsumoPlans } from '../../../constants/plans.constant';
import { selectIsUserNew } from '../../../features/auth/auth.slice';
import { selectChosenPlan, selectCurrency } from '../../../features/dashboard.slice';
import usePermission from '../../../hooks/usePermission';
import SiteFindBestPlanComponent from './SiteFindBestPlanComponent';

interface IPlan {
  type: 'monthly' | 'annually';
  name: string;
  code: { USD: string; EUR: string };
  price: {
    monthly: number | null;
    annually: number | null;
  };
  actionBtnText: string;
  features: FeaturesType[];
  level: number;
}

export interface ApiPlan {
  name: string;
  histories?: {
    paymentDateTime: string;
    paymentStatusEnum: string;
    paymentDetailMap: {
      invoice_id: string;
    };
    paymentNextDate: string;
  }[];
  type: string;
  details?: {
    plan_name: string;
    start_date: string;
    end_date: string;
    is_active: boolean;
  };
}

interface PlanLevel {
  name: string;
  level: number;
}

type CategoryTypes = 'Conversations per month' | 'Creations' | 'AI Models' | 'Voice Features' | 'Settings' | 'Customization' | 'Automation Tools' | 'Analytics';

type FeaturesType = {
  name: string;
  category: CategoryTypes;
  valueType: 'string' | 'boolean';
  value?: string | boolean;
};

export enum EPlanCodes {
  FREE = 'CHATBOT_FREE',
  FREE_EU = 'CHABOTC_LSH3RKBR5W',
  BASIC_MONTHLY = 'CHATBOT_BASIC_MONTHLY',
  BASIC_MONTHLY_EU = 'CHABOM_YXN1LILRWY',
  BASIC_ANNUAL = 'CHATBOT_BASIC_ANNUAL',
  BASIC_ANNUAL_EU = 'CHABOA_02OW2E8C4U',
  BASIC_ANNUAL_TRIAL = 'CHATBOT_BASIC_ANNUAL_TRIAL',
  ADVANCED_MONTHLY = 'CHATBOT_ADVANCED_MONTHLY',
  ADVANCED_MONTHLY_EU = 'CHABOM_OSMFCYDT0V',
  ADVANCED_ANNUAL = 'CHATBOT_ADVANCED_ANNUAL',
  ADVANCED_ANNUAL_EU = 'CHABOA_B42CDBJP56',
  ADVANCED_ANNUAL_TRIAL = 'CHATBOT_ADVANCED_ANNUAL_TRIAL',
  ENTERPRISE_ANNUAL_EU = 'CHABOA_Y8O3FH61GW',
  ENTERPRISE_ANNUAL = 'CHABOM_5Q5FNDRELE',
  ENTERPRISE_MONTHLY_EU = 'CHABOM_TX5B3V1J9V',
  ENTERPRISE_MONTHLY = 'CHABOM_8F83AHUURU',

  APPSUMO_TIER_1 = 'LB_APPSUMO_TIER1',
  APPSUMO_TIER_2 = 'LB_APPSUMO_TIER2',
  APPSUMO_TIER_3 = 'LB_APPSUMO_TIER3',
}

export const getHighestValidPlan = (plans: ApiPlan[], planLevels: PlanLevel[]): PlanLevel | null => {
  const currentDate = new Date();

  const validPlans = plans?.filter(plan => {
    if (plan.type === 'SUBSCRIPTION') {
      const validHistory = plan.histories?.find(history => {
        return (
          history.paymentStatusEnum === 'ACTIVE' ||
          history.paymentStatusEnum === 'SUCCEEDED' ||
          (history.paymentStatusEnum === 'CANCELED' && new Date(history.paymentNextDate) > currentDate)
        );
      });
      return !!validHistory;
    }
  });

  const validPlanLevels = planLevels?.filter(level => validPlans?.some(plan => plan.name === level.name)) || [];

  if (validPlanLevels.length === 0) {
    return null;
  }

  return validPlanLevels.reduce((highest, current) => (highest.level > current.level ? highest : current));
};

export const planLevelsArray = [
  { name: EPlanCodes.FREE, level: 1 },
  { name: EPlanCodes.FREE_EU, level: 1 },
  { name: EPlanCodes.BASIC_MONTHLY, level: 2 },
  { name: EPlanCodes.BASIC_MONTHLY_EU, level: 2 },
  { name: EPlanCodes.BASIC_ANNUAL, level: 3 },
  { name: EPlanCodes.BASIC_ANNUAL_EU, level: 3 },
  { name: EPlanCodes.ADVANCED_MONTHLY, level: 4 },
  { name: EPlanCodes.ADVANCED_MONTHLY_EU, level: 4 },
  { name: EPlanCodes.ADVANCED_ANNUAL, level: 5 },
  { name: EPlanCodes.ADVANCED_ANNUAL_EU, level: 5 },
  { name: EPlanCodes.ENTERPRISE_MONTHLY, level: 6 },
  { name: EPlanCodes.ENTERPRISE_MONTHLY_EU, level: 6 },
  { name: EPlanCodes.ENTERPRISE_ANNUAL, level: 7 },
  { name: EPlanCodes.ENTERPRISE_ANNUAL_EU, level: 7 },
];

const BillingCreatePage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isUserNew = useAppSelector(selectIsUserNew);
  const choosenPlanFromRegister = useAppSelector(selectChosenPlan);
  const currencySelected = useAppSelector(selectCurrency);

  const [planType] = useState<'monthly' | 'annually'>('annually');
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [hoveredIndex, sethoveredIndex] = useState(2);
  const permissions = usePermission();

  const [currency, setCurrency] = useState<'USD' | 'EUR'>(i18n.language.startsWith('en') ? 'USD' : 'EUR');

  useEffect(() => {
    if (currencySelected) {
      if (currencySelected == 'EU') {
        setCurrency('EUR');
      } else {
        setCurrency('USD');
      }
    }
  }, [currencySelected]);

  const initialPlans: IPlan[] = [
    {
      type: 'monthly',
      name: t('Pricing/key6'),
      code: { USD: EPlanCodes.FREE, EUR: EPlanCodes.FREE_EU },
      price: {
        monthly: 0,
        annually: 0,
      },
      actionBtnText: t('Pricing/key34'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '100',
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: '5',
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '72h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 1,
    },
    {
      type: 'annually',
      name: t('Pricing/key6'),
      code: { USD: EPlanCodes.FREE, EUR: EPlanCodes.FREE_EU },
      price: {
        monthly: 0,
        annually: 0,
      },
      actionBtnText: t('Pricing/key34'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '100',
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: '5',
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '72h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 1,
    },
    {
      type: 'monthly',
      name: t('Pricing/key7'),
      code: { USD: EPlanCodes.BASIC_MONTHLY, EUR: EPlanCodes.BASIC_MONTHLY_EU },
      price: {
        monthly: 19,
        annually: 13,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '500',
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '250',
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key32'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key32'),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key53'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 20 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 4, size: 20 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 3 }),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '48h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 2,
    },
    {
      type: 'annually',
      name: t('Pricing/key7'),
      code: { USD: EPlanCodes.BASIC_ANNUAL, EUR: EPlanCodes.BASIC_ANNUAL_EU },
      price: {
        monthly: 19,
        annually: 13,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '500',
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '250',
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key32'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key32'),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: '1',
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key53'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 20 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 4, size: 20 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 3 }),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 2 }),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '48h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 3,
    },
    {
      type: 'monthly',
      name: t('Pricing/key8'),
      code: {
        USD: EPlanCodes.ADVANCED_MONTHLY,
        EUR: EPlanCodes.ADVANCED_MONTHLY_EU,
      },
      price: {
        monthly: 39,
        annually: 26,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '1500',
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 50 }),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key43'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 5 }),
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key54'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 20 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 10, size: 100 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '48h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 4,
    },
    {
      type: 'annually',
      name: t('Pricing/key8'),
      code: {
        USD: EPlanCodes.ADVANCED_ANNUAL,
        EUR: EPlanCodes.ADVANCED_ANNUAL_EU,
      },
      price: {
        monthly: 39,
        annually: 26,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: '1500',
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 50 }),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key43'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 5 }),
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key54'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 20 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 10, size: 100 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key31', { count: 10 }),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: false,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '48h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: false,
        },
      ],
      level: 5,
    },
    {
      type: 'monthly',
      name: t('Pricing/key9'),
      code: {
        USD: EPlanCodes.ENTERPRISE_MONTHLY,
        EUR: EPlanCodes.ENTERPRISE_MONTHLY_EU,
      },
      price: {
        monthly: 370,
        annually: 299,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key44'),
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key55'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 50 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 18, size: 450 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '12h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
      ],
      level: 6,
    },
    {
      type: 'annually',
      name: t('Pricing/key9'),
      code: {
        USD: EPlanCodes.ENTERPRISE_ANNUAL,
        EUR: EPlanCodes.ENTERPRISE_ANNUAL_EU,
      },
      price: {
        monthly: 370,
        annually: 299,
      },
      actionBtnText: t('Pricing/key35'),
      features: [
        {
          name: t('Pricing/key10'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key11'),
          category: 'Conversations per month',
          valueType: 'string',
          value: t('Pricing/key44'),
        },
        {
          name: t('Pricing/key12'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key13'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key14'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key15'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key16'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key17'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key51'),
          category: 'Creations',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key52'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key55'),
        },
        {
          name: t('Pricing/scanLinks'),
          category: 'Creations',
          valueType: 'string',
          value: t('Pricing/key31', { count: 20 }),
        },
        {
          name: t('Pricing/key18'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key19'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key20'),
          category: 'AI Models',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key21'),
          category: 'AI Models',
          valueType: 'string',
          value: t('Pricing/key33', { count: 18, size: 450 }),
        },
        {
          name: t('Pricing/key22'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key23'),
          category: 'Voice Features',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key24'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key25'),
          category: 'Settings',
          valueType: 'string',
          value: t('Pricing/key42'),
        },
        {
          name: t('Pricing/key61'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key47'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key48'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key49'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key50'),
          category: 'Automation Tools',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key59'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key60'),
          category: 'Analytics',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key26'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key62'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key27'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key63'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key28'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key29'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key30'),
          category: 'Customization',
          valueType: 'boolean',
          value: true,
        },
        {
          name: t('Pricing/key56'),
          category: 'Settings',
          valueType: 'string',
          value: '12h',
        },
        {
          name: t('Pricing/key57'),
          category: 'Settings',
          valueType: 'boolean',
          value: true,
        },
      ],
      level: 7,
    },
  ];

  const [plans, setPlans] = useState<IPlan[]>(initialPlans);

  const buyPlan = (planName: string) => {
    setIsLoading(planName);

    useRedirectToPayMod({
      plan: planName,
      redirectUrl: `${MultiLanguageUrl[i18n.language] || 'en-US'}/verify-plan`,
    })
      .unwrap()
      .then(res => {
        return window.open(res, '_self');
      })
      .catch(err => {
        toast.error(err.data.message || err.message);
        setIsLoading(null);
        return;
      });
  };

  const getPlanInfo = useGetPlanInfoQuery();

  const [useRedirectToPayMod] = useRedirectToPayModMutation();

  const setHoverIndex = (item: number) => {
    sethoveredIndex(item);
  };

  useEffect(() => {
    if (getPlanInfo.data) {
      if (getPlanInfo.data.some((dr: any) => ApsumoPlans.some(x => x.name == dr.name))) {
        window.open('https://appsumo.com/products/louisebot');
        navigate(-1);
        return;
      }
    }
  }, [getPlanInfo.isSuccess]);

  //check permissions
  useEffect(() => {
    if (getPlanInfo.data) {
      let data = [...initialPlans];

      const highestPlan = getHighestValidPlan(getPlanInfo.data, planLevelsArray) || {
        name: EPlanCodes.FREE,
        level: 1,
      };

      // Following if filters plans by their level so user can see accessible plans after each plan upgrade
      if (!isUserNew && permissions?.billingPage && highestPlan) {
        const currentTopPlanLevel = highestPlan.level;

        if (currentTopPlanLevel)
          data = currentTopPlanLevel < 7 ? data.filter(plan => plan.level > currentTopPlanLevel) : data.filter(plan => plan.level >= currentTopPlanLevel);
      }

      if (choosenPlanFromRegister) {
        const indexOfChoosen = data.findIndex(item => item.code[currency].startsWith(choosenPlanFromRegister as string));

        if (indexOfChoosen > -1) setHoverIndex(indexOfChoosen - (indexOfChoosen > 4 ? 2 : 1));
      } else {
        const filterData = data.filter(dr => dr.type == planType);
        if (filterData.length <= 2 && hoveredIndex > filterData.length) {
          setHoverIndex(filterData.length);
        }
      }

      setPlans(data);
    }
  }, [getPlanInfo.isSuccess, planType, location.pathname]);

  // Toggle currency based on language changed
  useEffect(() => {
    i18n.on('languageChanged', lng => {
      setCurrency(lng.startsWith('en') ? 'USD' : 'EUR');
    });
  });

  let content;

  if (getPlanInfo.isLoading) content = <LoadingComponent />;

  if (getPlanInfo.isError) content = <ApiFailure refetch={getPlanInfo.refetch} />;

  if (getPlanInfo.isSuccess)
    content = (
      <div className="tw-flex tw-flex-col tw-gap-1 tw-bg-white tw-items-center md:tw-p-0 tw-p-4">
        <div className="tw-w-full">
          <div className="rounded tw-flex tw-flex-col tw-items-center gap-1 tw-bg-white  ">
            <SiteFindBestPlanComponent
              isLoadingSubscibe={isLoading as string}
              userPlan={plans}
              buyPlan={(value: string) => {
                buyPlan(value);
              }}
            />
          </div>
        </div>
      </div>
    );

  return <React.Fragment>{content}</React.Fragment>;
};

export default WrappedErrorBoundary(BillingCreatePage);
